import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthorizationModule } from '@wilson/authorization';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  ActivityLocationFilterComponent,
  FilterModule,
  MultiSelectParserService,
} from '@wilson/filter';
import { RoleAction, RolePermissionSubject } from '@wilson/interfaces';
import { ShiftTimelineFilterStateService } from '@wilson/shift-timeline/state';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { TimelineFilterHelperService } from '../timeline-filter-helper.service';
import { AbilityServiceSignal } from '@casl/angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faPlusSquare } from '@fortawesome/pro-regular-svg-icons';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';

@Component({
  selector: 'wilson-user-filters-group',
  templateUrl: './user-filters-group.component.html',
  styleUrls: ['./user-filters-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    NzCollapseModule,
    FeatureFlagsModule,
    FilterModule,
    ActivityLocationFilterComponent,
    AuthorizationModule,
    NzTooltipModule,
    NzSelectModule,
    FontAwesomeModule,
  ],
})
export class UserFiltersGroupComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly multiSelectParserService = inject(MultiSelectParserService);
  private readonly shiftTimelineFilterStateService = inject(
    ShiftTimelineFilterStateService,
  );
  protected readonly timelineFilterHelperService = inject(
    TimelineFilterHelperService,
  );

  RoleAction = RoleAction;
  RolePermissionSubject = RolePermissionSubject;
  protected can = inject(AbilityServiceSignal).can;
  readonly faPlusSquare = faPlusSquare;
  readonly faChevronUp = faChevronUp;
  readonly faChevronDown = faChevronDown;

  ngOnInit(): void {
    this.updateStateWithQueryParams();
  }

  orgUnitsChanged(orgIds: string[]): void {
    this.shiftTimelineFilterStateService.orgUnitIds = orgIds;
  }

  homeLocationChanged(homeLocationIds: string[]): void {
    this.shiftTimelineFilterStateService.homeLocationIds = homeLocationIds;
  }

  userRolesChanged(userRoles: string[]): void {
    this.shiftTimelineFilterStateService.roleIds = userRoles;
  }

  usersChanged(users: string[]): void {
    this.shiftTimelineFilterStateService.userIds = users;
  }

  professionChanged(professionIds: string[]): void {
    this.shiftTimelineFilterStateService.professionIds = professionIds;
  }

  labelsChanged(labels: string[]): void {
    this.shiftTimelineFilterStateService.labels = labels;
  }

  copyOrgUnitFromShift(): void {
    this.timelineFilterHelperService.copyField('shiftOrgUnit', 'orgUnit');
  }

  copyLabelFromShift(): void {
    this.timelineFilterHelperService.copyField('shiftLabels', 'labels');
  }

  copyProfessionFromShift(): void {
    this.timelineFilterHelperService.copyField('shiftProfession', 'profession');
  }

  private updateStateWithQueryParams(): void {
    const filters = (({
      orgUnit,
      role,
      users,
      profession,
      labels,
    }): {
      orgUnit: string;
      role: string;
      users: string;
      profession: string;
      labels: string;
    } => ({
      orgUnit,
      role,
      users,
      profession,
      labels,
    }))(this.route.snapshot.queryParams);

    this.orgUnitsChanged(
      this.multiSelectParserService.unparse(filters.orgUnit),
    );
    this.userRolesChanged(this.multiSelectParserService.unparse(filters.role));
    this.usersChanged(this.multiSelectParserService.unparse(filters.users));
    this.professionChanged(
      this.multiSelectParserService.unparse(filters.profession),
    );
    this.labelsChanged(this.multiSelectParserService.unparse(filters.labels));
  }
}
